import { XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { useViewHealthMarker } from '@/lib/view-health';

import { ConnectionStatusPanel } from './components/ConnectionStatusPanel';
import { ConnectSalesforceButton } from './components/ConnectSalesforceButton';
import { SalesforceCredentialsForm } from './components/SalesforceCredentialsForm';
import { useSalesforceStatus } from './hooks/useSalesforceStatus';
import { SalesforceIcon } from './SalesforceIcon';

// Every key here is a code the backend actually redirects with (the
// `salesforce_error=` sites in views/oauth_views.py). A code that is emitted but
// missing from this map falls through to DEFAULT_SALESFORCE_ERROR_MESSAGE, which
// tells an admin nothing about what to do next — so this map and those redirects
// must be kept in step.
//
// `person_accounts` and `invalid_host` were previously mapped here but are never
// emitted: the Person Accounts connect gate was replaced with detect-and-log, and
// My-Domain validation surfaces inline on the input as `hostError`, never as a
// redirect. Mapping a dead code costs nothing visible, which is exactly why the
// five real codes below went unnoticed as missing.
const SALESFORCE_ERROR_MESSAGES: Record<string, string> = {
  admin_required:
    'Only an account admin can connect Salesforce. Ask an admin to finish setting it up.',
  connect_race:
    'Another Salesforce connection finished first. Check the status above – if it didn’t connect, try again.',
  connection_failed:
    'Something went wrong finishing the Salesforce connection. Please try again.',
  credentials_missing:
    'Your Salesforce External Client App credentials are missing. Re-enter them below, then connect again.',
  identity_failed:
    'We connected to Salesforce but couldn’t read which org it was. Please try connecting again.',
  not_enabled: 'Salesforce integration is not enabled for this organization.',
  oauth_failed: "We couldn't complete Salesforce authentication. Try again.",
  token_exchange_failed:
    "We couldn't exchange the authorization code. Try connecting again.",
  user_mismatch:
    'That connection was started by a different account. Sign in as the person who started it, then try again.',
};

const DEFAULT_SALESFORCE_ERROR_MESSAGE =
  'An unexpected error occurred. Please try connecting again.';

export function SalesforceSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup('ahoy_other_admin');
  useViewHealthMarker('settings-section-ready', { ready: true });

  const [searchParams, setSearchParams] = useSearchParams();
  const status = useSalesforceStatus(orgId);

  const [oauthError, setOauthError] = useState<string | null>(null);
  const [isReplacingCredentials, setIsReplacingCredentials] = useState(false);

  const startReplacingCredentials = useCallback(
    () => setIsReplacingCredentials(true),
    []
  );
  const stopReplacingCredentials = useCallback(
    () => setIsReplacingCredentials(false),
    []
  );

  // Reset the transient "replace credentials" state when the admin switches orgs.
  // Without this the open replacement form carries over to the next org, offering to
  // overwrite a different org's credentials than the one it was opened for.
  useEffect(() => {
    setIsReplacingCredentials(false);
  }, [orgId]);

  useEffect(() => {
    const error = searchParams.get('salesforce_error');
    setOauthError(error);
    if (searchParams.get('salesforce_oauth') === 'success') {
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete('salesforce_oauth');
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const dismissError = useCallback(() => {
    setOauthError(null);
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('salesforce_error');
        return nextParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const connected = status.data?.connected ?? false;
  // Distinguish a failed status load from a still-loading one: on error `status.data` is
  // undefined, so every `?? default` below would otherwise silently assume the org's state
  // (defaulting credentialsConfigured to true) and expose the Connect card as if credentials
  // were configured. When the load actually failed we show a load-error and hide every setup
  // action until the real org state is known.
  const statusLoadFailed = status.isError;
  // Defaults to true so the Connect card — not the setup form — is what shows while
  // status is still loading. Flashing "set up your connected app" at an org that is
  // already configured is the worse of the two wrong states.
  const credentialsConfigured = status.data?.credentials_configured ?? true;
  // Authoritative OAuth redirect_uri from the backend (built from SITE_URL / the
  // local-proxy host). Passed to the setup form so it displays the exact value
  // Salesforce must register, rather than deriving one from the SPA origin.
  const callbackUrl = status.data?.callback_url;

  return (
    <div className='space-y-8'>
      <header>
        <div className='flex items-center gap-2'>
          <SalesforceIcon className='h-5 w-5 text-[var(--color-text-primary)]' />
          <h2 className='text-lg font-semibold text-fg-primary'>Salesforce</h2>
        </div>
        <p className='mt-0.5 text-sm text-fg-tertiary'>
          Import your Salesforce accounts, contacts, and opportunities into
          Ahoy.
        </p>
      </header>

      <div className='space-y-6'>
        {oauthError && (
          <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
            <div className='flex items-start gap-3'>
              <XCircle className='mt-0.5 h-5 w-5 shrink-0 text-utility-error-600' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-utility-error-900'>
                  We couldn't connect Salesforce
                </p>
                <p className='mt-1 text-sm text-utility-error-700'>
                  {SALESFORCE_ERROR_MESSAGES[oauthError] ??
                    DEFAULT_SALESFORCE_ERROR_MESSAGE}
                </p>
              </div>
              <CloseButton
                onPress={dismissError}
                label='Dismiss'
                size='sm'
                className='-my-1.5 -mr-1.5 shrink-0'
              />
            </div>
          </div>
        )}

        {statusLoadFailed && (
          <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
            <p className='text-sm text-utility-error-700'>
              Couldn’t load the Salesforce connection status. Refresh the page
              to try again.
            </p>
          </div>
        )}

        {!isAdmin && !connected && (
          <div className='rounded-lg border border-utility-warning-200 bg-utility-warning-50 p-4'>
            <p className='text-sm text-utility-warning-700'>
              Salesforce can only be connected by an account admin.
            </p>
          </div>
        )}

        {/* Hide every setup action until the org's real state is known — a failed status
            load must not fall through to the Connect card (which would misrepresent an
            unconfigured org as configured). */}
        {orgId !== undefined && !statusLoadFailed && (
          <>
            {connected && <ConnectionStatusPanel orgId={orgId} />}
            {/* An un-connected org needs one of three things: credentials first,
                the Connect card once it has them, or — because Salesforce lets an
                admin regenerate a consumer secret — a way to replace the stored
                pair without being stuck on the Connect card forever. */}
            {!connected && isAdmin && !credentialsConfigured && (
              <SalesforceCredentialsForm
                orgId={orgId}
                callbackUrl={callbackUrl}
              />
            )}
            {!connected &&
              isAdmin &&
              credentialsConfigured &&
              (isReplacingCredentials ? (
                <SalesforceCredentialsForm
                  orgId={orgId}
                  mode='replace'
                  callbackUrl={callbackUrl}
                  onCancel={stopReplacingCredentials}
                />
              ) : (
                <div className='space-y-3'>
                  <ConnectSalesforceButton orgId={orgId} />
                  <Button
                    color='link-gray'
                    size='sm'
                    onClick={startReplacingCredentials}
                  >
                    Replace credentials
                  </Button>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}
